import { FERTILITY_COST_CATALOG } from "./catalog";
import type { FertilityCostCatalog } from "./catalog";
import type { EstimateAreaOption } from "./locations";
import { US_CITY_ESTIMATE_AREA_OPTIONS } from "./locations";

// Deterministic resolution of a location the person picked (city, state, or
// country from the Places autocomplete) to a supported estimate area. It never
// depends on a network geocoder itself: the caller passes the resolved place
// (country code, optional state/city, coordinates) and this maps it to what the
// cost catalog can actually price, in three tiers:
//   - metro   : the country has city-level metro options (US today) -> exact or
//               nearest supported metro by haversine distance.
//   - country : the country has only a national estimate (FR/DE/ES) -> that
//               national estimate, labeled as such.
//   - none    : the country has no estimate at all -> unsupported.
export type CountryEstimateCoverage = "metro" | "country" | "none";

export type PlaceEstimateInput = {
  granularity: "city" | "state" | "country";
  /** Display label for the place the person picked (e.g., "Tucson, AZ"). */
  label: string;
  city?: string | null;
  /** State/province short code (e.g., "AZ") when the country uses them. */
  stateCode?: string | null;
  countryCode: string;
  countryName?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

export type PlaceEstimateResolution =
  | {
      status: "exact";
      requestedLabel: string;
      normalizedLabel: string;
      option: EstimateAreaOption;
    }
  | {
      status: "nearest";
      requestedLabel: string;
      normalizedLabel: string;
      option: EstimateAreaOption;
      nearestOption: EstimateAreaOption;
    }
  | {
      status: "country";
      requestedLabel: string;
      normalizedLabel: string;
      countryName: string;
      option: EstimateAreaOption;
    }
  | {
      status: "unsupported";
      requestedLabel: string;
      countryName: string;
      hint: string;
    };

type Coordinates = { latitude: number; longitude: number };

// Approximate downtown coordinates for the supported city options, keyed by
// option id. Kept out of the options' estimateLocation on purpose: adding
// latitude/longitude there would switch the estimator onto its nearby-clinics
// cascade for plain list selections. Exported so tests can assert it stays in
// sync with US_CITY_ESTIMATE_AREA_OPTIONS; a missing key would silently drop
// that city from nearest-fallback candidates.
export const SUPPORTED_CITY_COORDINATES: Record<string, Coordinates> = {
  "city-atlanta-ga": { latitude: 33.749, longitude: -84.388 },
  "city-austin-tx": { latitude: 30.267, longitude: -97.743 },
  "city-boston-ma": { latitude: 42.36, longitude: -71.058 },
  "city-chicago-il": { latitude: 41.878, longitude: -87.63 },
  "city-dallas-tx": { latitude: 32.777, longitude: -96.797 },
  "city-denver-co": { latitude: 39.739, longitude: -104.99 },
  "city-houston-tx": { latitude: 29.76, longitude: -95.37 },
  "city-los-angeles-ca": { latitude: 34.052, longitude: -118.244 },
  "city-miami-fl": { latitude: 25.762, longitude: -80.192 },
  "city-new-york-ny": { latitude: 40.713, longitude: -74.006 },
  "city-philadelphia-pa": { latitude: 39.953, longitude: -75.164 },
  "city-phoenix-az": { latitude: 33.448, longitude: -112.074 },
  "city-san-diego-ca": { latitude: 32.716, longitude: -117.161 },
  "city-san-francisco-ca": { latitude: 37.775, longitude: -122.419 },
  "city-seattle-wa": { latitude: 47.606, longitude: -122.332 },
  "city-washington-dc": { latitude: 38.907, longitude: -77.037 },
};

// Approximate geographic centers of the U.S. states plus DC, used to place a
// selected state (or a city we only know by state) when the Places result did
// not carry coordinates. City-level precision is not needed to pick the closest
// of the supported metros.
const US_STATE_CENTROIDS: Record<string, Coordinates> = {
  AL: { latitude: 32.8, longitude: -86.8 },
  AK: { latitude: 64.7, longitude: -152.3 },
  AZ: { latitude: 34.3, longitude: -111.7 },
  AR: { latitude: 34.9, longitude: -92.4 },
  CA: { latitude: 37.2, longitude: -119.3 },
  CO: { latitude: 39.0, longitude: -105.5 },
  CT: { latitude: 41.6, longitude: -72.7 },
  DE: { latitude: 39.0, longitude: -75.5 },
  DC: { latitude: 38.9, longitude: -77.0 },
  FL: { latitude: 28.6, longitude: -82.4 },
  GA: { latitude: 32.6, longitude: -83.4 },
  HI: { latitude: 20.3, longitude: -156.4 },
  ID: { latitude: 44.4, longitude: -114.6 },
  IL: { latitude: 40.0, longitude: -89.2 },
  IN: { latitude: 39.9, longitude: -86.3 },
  IA: { latitude: 42.1, longitude: -93.5 },
  KS: { latitude: 38.5, longitude: -98.4 },
  KY: { latitude: 37.5, longitude: -85.3 },
  LA: { latitude: 31.1, longitude: -92.0 },
  ME: { latitude: 45.4, longitude: -69.2 },
  MD: { latitude: 39.0, longitude: -76.8 },
  MA: { latitude: 42.3, longitude: -71.8 },
  MI: { latitude: 44.3, longitude: -85.4 },
  MN: { latitude: 46.3, longitude: -94.3 },
  MS: { latitude: 32.7, longitude: -89.7 },
  MO: { latitude: 38.4, longitude: -92.5 },
  MT: { latitude: 47.0, longitude: -109.6 },
  NE: { latitude: 41.5, longitude: -99.8 },
  NV: { latitude: 39.3, longitude: -116.6 },
  NH: { latitude: 43.7, longitude: -71.6 },
  NJ: { latitude: 40.2, longitude: -74.7 },
  NM: { latitude: 34.4, longitude: -106.1 },
  NY: { latitude: 42.9, longitude: -75.5 },
  NC: { latitude: 35.5, longitude: -79.4 },
  ND: { latitude: 47.4, longitude: -100.5 },
  OH: { latitude: 40.3, longitude: -82.8 },
  OK: { latitude: 35.6, longitude: -97.5 },
  OR: { latitude: 43.9, longitude: -120.6 },
  PA: { latitude: 40.9, longitude: -77.8 },
  RI: { latitude: 41.7, longitude: -71.6 },
  SC: { latitude: 33.9, longitude: -80.9 },
  SD: { latitude: 44.4, longitude: -100.2 },
  TN: { latitude: 35.9, longitude: -86.4 },
  TX: { latitude: 31.5, longitude: -99.3 },
  UT: { latitude: 39.3, longitude: -111.7 },
  VT: { latitude: 44.1, longitude: -72.7 },
  VA: { latitude: 37.5, longitude: -78.8 },
  WA: { latitude: 47.4, longitude: -120.4 },
  WV: { latitude: 38.6, longitude: -80.6 },
  WI: { latitude: 44.6, longitude: -89.9 },
  WY: { latitude: 43.0, longitude: -107.6 },
};

// Countries for which we offer city-level metro options, grouped from the
// supported-city list. Today only the U.S. has metros; adding metros for
// another country automatically extends the metro tier to it.
const METRO_OPTIONS_BY_COUNTRY = US_CITY_ESTIMATE_AREA_OPTIONS.reduce<
  Record<string, EstimateAreaOption[]>
>((acc, option) => {
  const code = option.estimateLocation.countryCode;
  (acc[code] ??= []).push(option);
  return acc;
}, {});

export function getUsStateCentroid(
  stateCode?: string | null,
): Coordinates | null {
  if (!stateCode) return null;
  return US_STATE_CENTROIDS[stateCode.toUpperCase()] ?? null;
}

function normalizeCountryCode(value: string): string {
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();
  if (
    lower === "usa" ||
    lower === "united states" ||
    lower === "united states of america"
  ) {
    return "US";
  }
  return trimmed.toUpperCase();
}

function getCountryScopeLineIds(
  countryCode: string,
  catalog: FertilityCostCatalog,
): Set<string> {
  const lineIds = new Set<string>();
  for (const entry of catalog.entries) {
    if (
      entry.location.scope === "country" &&
      entry.location.countryCode === countryCode
    ) {
      lineIds.add(entry.lineId);
    }
  }
  return lineIds;
}

export function getCountryEstimateCoverage(
  countryCode: string,
  catalog: FertilityCostCatalog = FERTILITY_COST_CATALOG,
): CountryEstimateCoverage {
  const normalized = normalizeCountryCode(countryCode);
  if (METRO_OPTIONS_BY_COUNTRY[normalized]) return "metro";
  // A country counts as priceable only if it can price the full baseline set
  // (the same lines the U.S. country rows cover). Countries with just a few
  // sparse policy/availability placeholder rows (FR/DE/ES today) are treated as
  // not-yet-covered rather than producing a partial estimate that throws on the
  // first uncovered line. This flips to "country" automatically once a country
  // gets a complete set of priced national rows.
  const baseline = getCountryScopeLineIds("US", catalog);
  if (baseline.size === 0) return "none";
  const countryLines = getCountryScopeLineIds(normalized, catalog);
  for (const lineId of baseline) {
    if (!countryLines.has(lineId)) return "none";
  }
  return "country";
}

function haversineMiles(from: Coordinates, to: Coordinates) {
  const earthRadiusMiles = 3958.8;
  const fromLat = (from.latitude * Math.PI) / 180;
  const toLat = (to.latitude * Math.PI) / 180;
  const deltaLat = ((to.latitude - from.latitude) * Math.PI) / 180;
  const deltaLon = ((to.longitude - from.longitude) * Math.PI) / 180;
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(fromLat) *
      Math.cos(toLat) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);

  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getSupportedAreaLabel(option: EstimateAreaOption) {
  const stateCode = option.estimateLocation.stateCode;
  return stateCode ? `${option.label}, ${stateCode}` : option.label;
}

function slugify(value: string) {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "location"
  );
}

export function findNearestSupportedCityOption(
  coordinates: Coordinates,
  metros: EstimateAreaOption[] = US_CITY_ESTIMATE_AREA_OPTIONS,
): EstimateAreaOption {
  let nearest = metros[0]!;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const option of metros) {
    const cityCoordinates = SUPPORTED_CITY_COORDINATES[option.id];
    if (!cityCoordinates) continue;
    const distance = haversineMiles(coordinates, cityCoordinates);
    if (distance < nearestDistance) {
      nearest = option;
      nearestDistance = distance;
    }
  }
  return nearest;
}

function countryResolution({
  requestedLabel,
  countryCode,
  countryName,
}: {
  requestedLabel: string;
  countryCode: string;
  countryName: string;
}): PlaceEstimateResolution {
  return {
    status: "country",
    requestedLabel,
    normalizedLabel: requestedLabel,
    countryName,
    option: {
      id: `custom-${slugify(requestedLabel)}-${countryCode.toLowerCase()}`,
      label: requestedLabel,
      detail: `${countryName} national estimate`,
      source: "custom",
      estimateAreaLabel: countryName,
      estimateLocation: { countryCode },
    },
  };
}

export function resolvePlaceEstimateArea(
  input: PlaceEstimateInput,
  catalog: FertilityCostCatalog = FERTILITY_COST_CATALOG,
): PlaceEstimateResolution {
  const countryCode = normalizeCountryCode(input.countryCode);
  const countryName = input.countryName?.trim() || countryCode;
  const requestedLabel = input.label.trim() || countryName;
  const coverage = getCountryEstimateCoverage(countryCode, catalog);

  if (coverage === "none") {
    return {
      status: "unsupported",
      requestedLabel,
      countryName,
      hint: `We don't have estimates for ${countryName} yet.`,
    };
  }

  // A whole-country pick, or a country we only price nationally, uses the
  // national estimate rather than guessing a metro.
  if (coverage === "country" || input.granularity === "country") {
    return countryResolution({ requestedLabel, countryCode, countryName });
  }

  const metros = METRO_OPTIONS_BY_COUNTRY[countryCode]!;
  const requestedCity = input.city?.trim();
  const requestedState = input.stateCode?.trim().toUpperCase();

  if (requestedCity && requestedState) {
    const exact = metros.find(
      (option) =>
        option.label.toLowerCase() === requestedCity.toLowerCase() &&
        option.estimateLocation.stateCode === requestedState,
    );
    if (exact) {
      const label = getSupportedAreaLabel(exact);
      return {
        status: "exact",
        requestedLabel: label,
        normalizedLabel: label,
        option: exact,
      };
    }
  }

  const coords =
    input.latitude != null && input.longitude != null
      ? { latitude: input.latitude, longitude: input.longitude }
      : getUsStateCentroid(requestedState);
  if (!coords) {
    // Metro country but nothing to place it by: fall back to the national
    // estimate rather than silently picking a random metro.
    return countryResolution({ requestedLabel, countryCode, countryName });
  }

  const nearestOption = findNearestSupportedCityOption(coords, metros);
  const nearestLabel = getSupportedAreaLabel(nearestOption);
  return {
    status: "nearest",
    requestedLabel,
    normalizedLabel: requestedLabel,
    option: {
      id: `custom-${slugify(requestedLabel)}`,
      label: requestedLabel,
      detail: `Closest estimate: ${nearestLabel}`,
      source: "custom",
      estimateAreaLabel: nearestLabel,
      estimateLocation: { ...nearestOption.estimateLocation },
    },
    nearestOption,
  };
}
